'use client';

import type { Dispatch, ReactNode } from 'react';
import { createContext, useMemo, useState, useEffect, useCallback, useReducer } from 'react';
import { getPayloadForFetchingIbkDailyAvailability } from '@/utils/ibk/getIbkAvailabilityPayload';
import type { IbkDailyAvailabilityRequestData, IbkDailyAvailabilityData } from '@/models/IbkData';
import type { IbkDailyAvailabilityAction } from '@/models/IbkAction';
import { RoomDates, MEETING_ROOM_FIELD_IDS, GUEST_ROOM_FIELD_IDS } from '@/providers/IbkProvider';

export type IbkAvailabilityCalendarContextType = {
  IBK_AVAILABILITY_CALENDAR_DATA: {
    ibkDailyAvailabilityState: IbkDailyAvailabilityRequestData;
    ibkDailyAvailabilityData: Array<IbkDailyAvailabilityData>;
    ibkDailyAvailabilityDataLoading: boolean;
    ibkDailyAvailabilityDataError: boolean;
    sleepingCheckoutOnlyDates: string[];
    isIbkMeetingRoomSearched: boolean;
    isIbkGuestRoomSearched: boolean;
  };
  IBK_AVAILABILITY_CALENDAR_ACTIONS: {
    ibkDailyAvailabilityDispatch: Dispatch<IbkDailyAvailabilityAction>;
    getIBKDailyCalendarAvailability: () => void;
  };
};

type IbkAvailabilityCalendarProps = {
  venueId: string;
  children: ReactNode;
};

export const IbkAvailabilityCalendarContext = createContext({} as IbkAvailabilityCalendarContextType);

function ibkAvailabilityCalendarReducer(
  state: IbkDailyAvailabilityRequestData,
  action: IbkDailyAvailabilityAction
): IbkDailyAvailabilityRequestData {
  switch (action.type) {
    case 'SET_AVAILABILITY_CALENDAR_MONTH_START_DATE':
      return { ...state, startDate: action.payload.startDate };
    case 'SET_AVAILABILITY_CALENDAR_MONTH_END_DATE':
      return { ...state, endDate: action.payload.endDate };
    case 'SET_AVAILABILITY_CALENDAR_SEARCH_PARAMS':
      return { ...state, searchParams: action.payload.searchParams };
    default:
      throw new Error();
  }
}

// Provide the context for the IBK daily availability calendar for meeting and sleeping space availability
export default function IbkAvailabilityCalendarProvider({ venueId, children }: IbkAvailabilityCalendarProps) {
  const initialState: IbkDailyAvailabilityRequestData = useMemo(() => {
    const calendarInitialState = {
      startDate: '',
      endDate: '',
      searchParams: {}
    };
    return calendarInitialState;
  }, []);

  const [ibkDailyAvailabilityState, ibkDailyAvailabilityDispatch] = useReducer(
    ibkAvailabilityCalendarReducer,
    initialState
  );

  const { startDate, endDate, searchParams } = ibkDailyAvailabilityState;

  const meetingRoomAttendeesFromWidgetModel = parseInt(searchParams?.[MEETING_ROOM_FIELD_IDS.ATTENDEES]?.value, 10);
  const meetingRoomDates: RoomDates = searchParams?.[MEETING_ROOM_FIELD_IDS.DATES]?.value;

  const guestRoomCountFromWidgetModal = parseInt(searchParams?.[GUEST_ROOM_FIELD_IDS.ROOM_COUNT]?.value, 10);
  const guestRoomDates: RoomDates = searchParams?.[GUEST_ROOM_FIELD_IDS.DATES]?.value;

  const isMeetingRoomFilledOut = !!(meetingRoomAttendeesFromWidgetModel && meetingRoomDates);
  const isGuestRoomFilledOut = !!(guestRoomCountFromWidgetModal && guestRoomDates);

  const [ibkDailyAvailabilityData, setIbkDailyAvailabilityData] = useState<IbkDailyAvailabilityData[]>([]);
  const [ibkDailyAvailabilityDataLoading, setIbkDailyAvailabilityDataLoading] = useState<boolean>(false);
  const [ibkDailyAvailabilityDataError, setIbkDailyAvailabilityDataError] = useState<boolean>(false);
  const [ibkDailySleepingCheckoutOnlyDates, setIbkDailySleepingCheckoutOnlyDates] = useState<string[]>([]);
  const [isIbkMeetingRoomSearched, setIsIbkMeetingRoomSearched] = useState<boolean>(isMeetingRoomFilledOut);
  const [isIbkGuestRoomSearched, setIsIbkGuestRoomSearched] = useState<boolean>(isGuestRoomFilledOut);

  const payload = getPayloadForFetchingIbkDailyAvailability({
    startDate,
    endDate,
    isMeetingRoomFilledOut,
    isGuestRoomFilledOut,
    venueId
  });

  const onCompleted = useCallback(
    data => {
      const sortedData = data.result.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
      if (isGuestRoomFilledOut) {
        const newCheckoutOnlyDates = [...ibkDailySleepingCheckoutOnlyDates];
        sortedData.forEach((item, index) => {
          if (index > 0) {
            const previousItem = sortedData[index - 1];
            if (
              !item?.sleepingAvailability?.cheapestSleepingPrice &&
              previousItem?.sleepingAvailability?.cheapestSleepingPrice
            ) {
              newCheckoutOnlyDates.push(item.startDate);
            }
          }
        });
        setIbkDailySleepingCheckoutOnlyDates(newCheckoutOnlyDates);
      }
      setIbkDailyAvailabilityData(sortedData);
      setIbkDailyAvailabilityDataLoading(false);
      setIbkDailyAvailabilityDataError(false);
    },
    [isGuestRoomFilledOut, ibkDailySleepingCheckoutOnlyDates]
  );

  const getIBKDailyCalendarAvailability = useCallback(() => {
    setIbkDailyAvailabilityDataLoading(true);
    async function fetchIbkDailyAvailability() {
      if (!payload) return;
      try {
        const response = await fetch('/venues/api/ibk-daily-availability', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        if (response.ok) {
          const data = await response.json();
          onCompleted(data);
        } else {
          setIbkDailyAvailabilityDataError(true);
          setIbkDailyAvailabilityData([]);
          setIbkDailyAvailabilityDataLoading(false);
        }
      } catch (e) {
        setIbkDailyAvailabilityDataLoading(false);
        setIbkDailyAvailabilityDataError(true);
      }
    }
    if (startDate && endDate) {
      fetchIbkDailyAvailability();
    }
  }, [startDate, endDate, onCompleted, payload]);

  useEffect(() => {
    if (startDate && endDate && !ibkDailyAvailabilityDataLoading) getIBKDailyCalendarAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, searchParams]);

  useEffect(() => {
    setIsIbkMeetingRoomSearched(isMeetingRoomFilledOut);
  }, [isMeetingRoomFilledOut, setIsIbkMeetingRoomSearched]);

  useEffect(() => {
    setIsIbkGuestRoomSearched(isGuestRoomFilledOut);
  }, [isGuestRoomFilledOut, setIsIbkGuestRoomSearched]);

  const value = useMemo(
    () => ({
      IBK_AVAILABILITY_CALENDAR_DATA: {
        ibkDailyAvailabilityState,
        ibkDailyAvailabilityData,
        ibkDailyAvailabilityDataLoading,
        ibkDailyAvailabilityDataError,
        sleepingCheckoutOnlyDates: ibkDailySleepingCheckoutOnlyDates,
        isIbkMeetingRoomSearched,
        isIbkGuestRoomSearched
      },
      IBK_AVAILABILITY_CALENDAR_ACTIONS: {
        ibkDailyAvailabilityDispatch,
        getIBKDailyCalendarAvailability
      }
    }),
    [
      ibkDailyAvailabilityState,
      ibkDailyAvailabilityData,
      ibkDailyAvailabilityDataLoading,
      ibkDailyAvailabilityDataError,
      ibkDailySleepingCheckoutOnlyDates,
      isIbkMeetingRoomSearched,
      isIbkGuestRoomSearched,
      getIBKDailyCalendarAvailability
    ]
  );

  return <IbkAvailabilityCalendarContext.Provider value={value}>{children}</IbkAvailabilityCalendarContext.Provider>;
}
